/**
 * Per-slot serialisation with per-target de-duplication.
 *
 * One camera slot, two problems that need OPPOSITE answers:
 *
 *  1. A repeated tap on "Connecting…" must not start a second connect. Concurrent
 *     `gp_camera_init` inside one WASM/libusb instance corrupts shared state and crashes
 *     the module, so a repeat tap JOINS the attempt already running.
 *
 *  2. Switching the slot to a DIFFERENT camera is a different request and must actually
 *     run. Keying only on the slot — the regression shipped on 2026-09-16 — made a switch
 *     join the in-flight attempt, so the new body was never connected: `disconnect()` had
 *     already reset deviceType to 'webcam', nothing set it to the new camera, and the
 *     preview loop threw CameraUnavailableError forever while the UI showed the new
 *     camera as connected.
 *
 * So: same slot + same target joins; same slot + different target queues behind the
 * running attempt and then runs; different slots are independent. Nothing on one slot
 * ever overlaps, which is what protects the WASM instance.
 */
export interface SerialSingleFlight<K extends string, T> {
  /**
   * Run `fn` for `key`. `token` identifies the target: an identical token joins the
   * attempt in flight; a different one waits for it, then runs.
   */
  run: (key: K, token: string, fn: () => Promise<T>) => Promise<T>
  /** Is an attempt for this slot in flight? */
  isInFlight: (key: K) => boolean
  /** The target currently in flight for this slot, if any. */
  inFlightToken: (key: K) => string | undefined
}

/** Call `fn` now, turning a synchronous throw into a rejection. */
function startNow<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return fn()
  } catch (err) {
    return Promise.reject(err)
  }
}

export function createSerialSingleFlight<K extends string, T>(): SerialSingleFlight<K, T> {
  const current = new Map<K, { token: string; promise: Promise<T> }>()
  // Tail of each slot's queue — everything on a slot runs strictly in order.
  const tail = new Map<K, Promise<unknown>>()

  return {
    isInFlight: (key): boolean => current.has(key),
    inFlightToken: (key): string | undefined => current.get(key)?.token,
    run: (key, token, fn): Promise<T> => {
      const running = current.get(key)
      // Same slot, same target — a repeat tap. Join it.
      if (running && running.token === token) return running.promise

      // Different target (or the slot is idle): queue behind whatever is on this slot,
      // and run whether or not that one succeeded. A failed connect must never block the
      // operator's next choice.
      const prior = tail.get(key)
      // An idle slot starts immediately rather than after a microtask hop, so the first
      // connect behaves exactly as an unqueued call would.
      const started = prior
        ? prior.then(
            () => fn(),
            () => fn()
          )
        : startNow(fn)
      const entry = { token, promise: started }
      // Registered immediately, so a repeat tap on the NEW target joins this queued
      // attempt instead of adding a third.
      current.set(key, entry)

      const settled = started.finally(() => {
        // Only clear if still current — a later attempt must never be cleared by an
        // earlier one settling.
        if (current.get(key) === entry) current.delete(key)
      })
      // The queue only needs completion, not success. `settled` still rejects for the
      // caller, so this introduces no unhandled rejection.
      const drained = settled.catch(() => undefined)
      tail.set(key, drained)
      // Forget the queue once it drains, so the next connect on an idle slot starts
      // immediately instead of chaining onto a long-finished promise.
      void drained.then(() => {
        if (tail.get(key) === drained) tail.delete(key)
      })
      return settled
    }
  }
}
